import React, {useDebugValue} from 'react';

import classNames from 'classnames';

import {useSearchFormContext} from '../../../hooks/useSearchFormContext';
import {useTrackingContext} from '../../../hooks/useTrackingContext';

import {submitForm} from '../../../utils/form';
import {
    getGuestsFieldInputLabel,
    getGuestsFieldAdultsInputLabel,
    getGuestsFieldChildrenInputLabel,
    getGuestsFieldBedroomsInputLabel,
    shouldGuestsFieldAutoSubmitOnClose,
    showGuestsInputFieldError,
    showGuestsInputIcon
} from '../../../utils/guests';
import {runGuestPickerBedroomsExperiment} from '../../../utils/experiments';

import {SearchIcon} from '../../SearchIcon/SearchIcon';

const classNamespace = 'search-form';

const fieldClassName = `${classNamespace}__field`;
const inputFieldClassName = `${classNamespace}__input-field`;
const inputFieldErrorClassName = `${inputFieldClassName}--error`;
const detailsFieldClassName = `${classNamespace}__details-field`;
const detailsInputFieldClassName = `${classNamespace}__details-input-field`;
const guestsDetailsInputFieldClassName = `${classNamespace}__guests-details-input-field`;

const guestsPickerClassName = `${classNamespace}__guests-picker`;

function useComponent() {
    const {
        systemOptions,
        mediaSettings,
        mediaSettings: {
            isMobile
        },
        abacusStore,
        edapOptions,
        latestSearchValues,
        formActions,
        formEvents,
        formOptions,
        destinationActions,
        destinationActions: {
            setIsTypeaheadInputFocus
        },
        latestDestinationValues,
        datesActions: {
            setIsOpen: setDatesIsOpen
        },
        latestDatesValues,
        guestsActions: {
            setIsOpen: setGuestsIsOpen,
            setCurrentGuestsValuesLatest,
            setLatestGuestsValues,
            setAllGuestsValuesDefault
        },
        guestsEvents: {
            onChange,
            onClear,
            onClose,
            onExit,
            onOpen
        },
        guestsOptions,
        guestsOptions: {
            actionLocation,
            adultsMax,
            adultsMin,
            bedroomsMax,
            bedroomsMin,
            centered,
            childrenMax,
            childrenMin,
            disableTextInput,
            fadeOut,
            fadeOutDuration,
            id,
            inputIconName,
            name,
            placement,
            positionIconRight,
            showBedroomsOption,
            showClearButton,
            showContinue,
            showPetOption,
            wdioPrefix
        },
        guestsSettings: {
            disableButton,
            isOpen,
            readOnly
        },
        guestsTranslations,
        guestsTranslations: {
            label,
            modalTitle,
            petLabel,
            petNoLabel,
            petYesLabel,
            petMessage,
            applyButtonLabel,
            clearButtonLabel,
            continueButtonLabel
        },
        latestGuestsValues,
        latestGuestsValues: {
            adultsCount,
            childrenCount,
            bedroomsCount,
            petIncluded
        }
    } = useSearchFormContext();

    const trackingContext = useTrackingContext();

    const className = classNames(fieldClassName, inputFieldClassName, {
        [inputFieldErrorClassName]: showGuestsInputFieldError(mediaSettings, formOptions, guestsOptions, latestGuestsValues)
    }, detailsFieldClassName, detailsInputFieldClassName, guestsDetailsInputFieldClassName);

    const pickerClassName = classNames(guestsPickerClassName);

    const inputIcon = showGuestsInputIcon(formOptions, guestsOptions) && (
        <SearchIcon name={inputIconName}/>
    ) || null;

    const guestLabel = getGuestsFieldInputLabel(guestsTranslations, latestGuestsValues, showBedroomsOption);

    const adultsLabel = getGuestsFieldAdultsInputLabel(guestsTranslations, latestGuestsValues);
    const childrenLabel = getGuestsFieldChildrenInputLabel(guestsTranslations, latestGuestsValues);
    const bedroomsLabel = getGuestsFieldBedroomsInputLabel(guestsTranslations, latestGuestsValues);

    const formSuffixElement = petIncluded && (
        <p className="text-center text-muted">{petMessage}</p>
    ) || null;

    function handleChange(...args) {
        const [
            changedKey,
            changedValue
        ] = args;

        const changedGuestsValues = {
            [changedKey]: changedValue
        };

        setLatestGuestsValues(changedGuestsValues);

        onChange(...args);
    }

    function handleClear(...args) {
        setAllGuestsValuesDefault();

        onClear(...args);
    }

    function handleClose(...args) {
        setCurrentGuestsValuesLatest();

        setGuestsIsOpen(false);

        if (shouldGuestsFieldAutoSubmitOnClose(mediaSettings, formOptions, guestsOptions, abacusStore, systemOptions, latestDestinationValues, latestDatesValues)) {
            submitForm(trackingContext, systemOptions, abacusStore, edapOptions, latestSearchValues, formActions, formEvents, destinationActions, latestDestinationValues, latestDatesValues, latestGuestsValues);
        }

        onClose(...args);
    }

    function handleExit(...args) {
        setCurrentGuestsValuesLatest();

        setGuestsIsOpen(false);

        onExit(...args);
    }

    function handleOpen(...args) {
        setIsTypeaheadInputFocus(false);
        setDatesIsOpen(false);
        setGuestsIsOpen(true);

        runGuestPickerBedroomsExperiment({shouldLogExperiment: true}, abacusStore);

        onOpen(...args);
    }

    const result = {
        className,
        actionLocation,
        adultsCount,
        adultsLabel,
        adultsMax,
        adultsMin,
        applyButtonLabel,
        bedroomsCount,
        bedroomsLabel,
        bedroomsMax,
        bedroomsMin,
        centered,
        childrenCount,
        childrenLabel,
        childrenMax,
        childrenMin,
        clearButtonLabel,
        continueButtonLabel,
        disableButton,
        disableTextInput,
        fadeOut,
        fadeOutDuration,
        formSuffixElement,
        guestLabel,
        id,
        inputIcon,
        isMobile,
        isOpen,
        label,
        modalTitle,
        name,
        onChange: handleChange,
        onClear: handleClear,
        onClose: handleClose,
        onExit: handleExit,
        onOpen: handleOpen,
        petIncluded,
        petLabel,
        petNoLabel,
        petYesLabel,
        pickerClassName,
        placement,
        positionIconRight,
        readOnly,
        showBedroomsOption,
        showClearButton,
        showContinue,
        showPetOption,
        wdioPrefix
    };

    useDebugValue(result);

    return result;
}

export {useComponent};
